<template>
  <div class="p-2" id="healing_log">
    <!-- Date Filter -->
    <div class="date-filter mb-3">

      <!-- Desktop: pill buttons -->
      <div class="date-filter-presets">
        <button
          v-for="p in presets"
          :key="p.value"
          class="preset-btn"
          :class="{ active: datePreset === p.value }"
          @click="setPreset(p.value)"
        >{{ p.label }}</button>
      </div>

      <!-- Mobile: styled select dropdown -->
      <div class="date-filter-select-wrap">
        <i class="fas fa-calendar-alt date-filter-select-icon"></i>
        <select class="date-filter-select" :value="datePreset === null ? 'null' : datePreset" @change="setPreset($event.target.value === 'null' ? null : ($event.target.value === 'custom' ? 'custom' : Number($event.target.value)))">
          <option v-for="p in presets" :key="p.value" :value="p.value === null ? 'null' : p.value">{{ p.label }}</option>
        </select>
        <i class="fas fa-chevron-down date-filter-select-chevron"></i>
      </div>

      <!-- Custom date inputs (both views) -->
      <div v-if="datePreset === 'custom'" class="date-filter-custom">
        <input type="date" v-model="customDateFrom" class="date-input" :max="customDateTo || today" />
        <span class="date-sep">—</span>
        <input type="date" v-model="customDateTo" class="date-input" :min="customDateFrom" :max="today" />
        <button class="preset-btn active" @click="applyCustom"><i class="fas fa-sync-alt"></i></button>
      </div>

    </div>
    <!-- Stats -->
    <div class="contraction-stats mb-4">
      <div class="stat-card">
        <div class="stat-value">
          <i v-if="stats === null" class="fas fa-spinner fa-spin"></i>
          <span v-else>{{ stats.user.contractions.total_logged }}</span>
        </div>
        <div class="stat-label">Logged </div>
      </div>
      <div class="stat-card">
        <div class="stat-value">
          <i v-if="stats === null" class="fas fa-spinner fa-spin"></i>
          <span v-else>{{ stats.user.contractions.total_processed }}</span>
        </div>
        <div class="stat-label">Processed </div>
      </div>
      <div class="stat-card">
        <div class="stat-value">
          <i v-if="stats === null" class="fas fa-spinner fa-spin"></i>
          <span v-else>{{ stats.user.supported_others }}</span>
        </div>
        <div class="stat-label">Supported Others</div>
      </div>
    </div>

    <!-- Active Processing Requests -->
    <div v-if="activeRequests.length > 0" class="active-requests mb-4">
      <div class="apr-toggle" @click="activeRequestsExpanded = !activeRequestsExpanded">
        <div class="apr-toggle-left">
          <span class="apr-pulse"></span>
          <i class="fas fa-hands-helping apr-toggle-icon"></i>
          <span class="apr-toggle-title">Active Processing Requests</span>
          <span class="apr-count-badge">{{ activeRequests.length }}</span>
        </div>
        <i class="fas apr-chevron" :class="activeRequestsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </div>
      <div v-if="activeRequestsExpanded" class="active-requests-list">
        <div
          v-for="req in activeRequests"
          :key="'apr-' + req.processing_request_id"
          class="active-request-row"
        >
          <!-- Actions dropdown -->
          <b-dropdown left no-caret variant="link" size="sm" class="apr-dropdown" boundary="window" @click.native.stop>
            <template #button-content>
              <i class="fas fa-chevron-down apr-dropdown-icon"></i>
            </template>
            <b-dropdown-item :to="'/chatbox/' + req.target_user_id + '?from=/contractions'" class="apr-action-primary">
              <i class="fas fa-comment-dots mr-2"></i> Open Messages
            </b-dropdown-item>
            <b-dropdown-item :to="'/member/' + req.target_user_id + '?from=/contractions'">
              <i class="fas fa-user mr-2"></i> View Profile
            </b-dropdown-item>
            <b-dropdown-item @click="openForRequest(req)">
              <i class="fas fa-check-circle mr-2"></i> Resolve {{ req.contraction_id ? 'Contraction' : '' }}
            </b-dropdown-item>
          </b-dropdown>

          <!-- Avatar -->
          <div class="apr-avatar-wrap">
            <img
              :src="req.target_avatar || 'https://media.iamconnected.com/profile_images/default.png'"
              class="apr-avatar"
              :alt="req.target_name"
              @error="$event.target.src = 'https://media.iamconnected.com/profile_images/default.png'"
            />
          </div>

          <!-- Body -->
          <div class="apr-body">
            <div class="apr-name">{{ req.target_name }}</div>
            <div class="apr-contraction">{{ req.contraction }}</div>
          </div>

          <!-- Status badge -->
          <span class="active-request-badge">{{ req.status }}</span>
        </div>
      </div>
    </div>

    <!-- Search -->
    <div class="">
      <div class=" mb-2">
        <b-input-group>
          <b-form-input style="font-size: 0.72rem"
            v-model="search_text"
            placeholder="Search Contractions..."
          ></b-form-input>
          <b-input-group-append>
              <b-button style="font-size: 0.72rem"
              variant="outline-secondary"
              class="m-0"
              @click="clearSearchText"
            >
              <i class="fas fa-times"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
    </div>

    <div v-if="searching == 1" class="text-muted">
      <i class="fas fa-atom fa-spin"></i> searching
    </div>

    <template v-if="feelingContractions.length > 0">

      <!-- Unprocessed section -->
      <div class="mb-3">
        <div class="section-label">Unprocessed</div>
        <div v-if="unprocessedContractions.length > 0" class="contraction-list-scroll">
          <b-list-group>
            <healing-log-item
              v-for="(contraction, index) in unprocessedContractions"
              :key="'unprocessed-' + index"
              :contraction="contraction"
              :priority="contractionPriority(contraction)"
              :pending-resolve-id="pendingContractionId"
              @request-resolve="openForContraction(contraction)"
              @refreshFeelings="onRefresh"
            />
          </b-list-group>
        </div>
        <div v-else class="d-flex align-items-center gap-2 px-3 py-2 rounded border border-warning text-muted" style="border-style: dashed !important; background: rgba(214,182,116,0.05); font-size: 0.75rem;">
          <i class="fas fa-inbox mr-2" style="color: rgba(214,182,116,0.5);"></i>
          <span class="font-italic">No unprocessed contractions</span>
        </div>
      </div>

      <!-- Processed section -->
      <div>
        <div class="section-label section-label--processed">Processed</div>
        <div v-if="processedContractions.length > 0" class="contraction-list-scroll">
          <b-list-group>
            <healing-log-item
              v-for="(contraction, index) in processedContractions"
              :key="'processed-' + index"
              :contraction="contraction"
              :pending-resolve-id="pendingContractionId"
              @request-resolve="openForContraction(contraction)"
              @refreshFeelings="onRefresh"
            />
          </b-list-group>
        </div>
        <div v-else class="d-flex align-items-center gap-2 px-3 py-2 rounded border text-muted" style="border: #dee2e6;font-size: 0.75rem;">
          <i class="fas fa-check-circle mr-2" style="color: rgba(214,182,116,0.5);"></i>
          <span class="font-italic">No processed contractions</span>
        </div>
      </div>

    </template>

    <div v-else>
      <p class="text-muted text-center">No Contractions found</p>
    </div>

    <!-- Load More -->
    <div v-if="hasMore && !searching" class="text-center mt-3 mb-2">
      <b-button
        variant="outline-secondary"
        size="sm"
        :disabled="loadingMore"
        @click="feelingsGet('more')"
        class="load-more-btn"
      >
        <i v-if="loadingMore" class="fas fa-spinner fa-spin mr-1"></i>
        <span v-else><i class="fas fa-chevron-down mr-1"></i></span>
        {{ loadingMore ? 'Loading...' : 'Load More' }}
      </b-button>
    </div>

    <ack-modal ref="ackModal" @resolved="onRefresh" @cancelled="pendingContractionId = null" />

  </div>
</template>

<script>
import axios from "@/axios";
import HealingLogItem from "./HealingLogItem.vue";
import AckModal from "./AckModal.vue";

export default {
  name: "HealingLog",
  components: { HealingLogItem, AckModal },
  props: {
    userid: {
      type: Number,
      default: null,
    },
    limit: {
      type: Number,
      default: 14,
    },
    readonly: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      search_text: "",
      searching: 1,
      searchFeelingsContractions: [],
      total: null,
      noMore: false,
      currentPage: 1,
      loadingMore: false,
      activeRequests: [],
      activeRequestsExpanded: true,
      stats: null,
      pendingContractionId: null,

      datePreset: null,
      customDateFrom: "",
      customDateTo: "",
      today: new Date().toISOString().slice(0, 10),
      presets: [
        { label: "All", value: null },
        { label: "Last 7 Days", value: 7 },
        { label: "Last 30 Days", value: 30 },
        { label: "Last Year", value: 365 },
        { label: "Custom", value: "custom" },
      ],
    };
  },
  computed: {
    feelingContractions() {
      return this.searchFeelingsContractions;
    },
    unprocessedContractions() {
      return this.feelingContractions.filter((c) => !c.resolved_at);
    },
    processedContractions() {
      return this.feelingContractions.filter((c) => !!c.resolved_at);
    },
    hasMore() {
      return !this.noMore && this.total !== null && this.searchFeelingsContractions.length < this.total;
    },
  },
  methods: {
    setPreset(value) {
      this.datePreset = value;
      if (value !== "custom") {
        this.feelingsGet("latest");
        this.statsGet();
      }
    },

    applyCustom() {
      this.feelingsGet("latest");
      this.statsGet();
    },

    feelingsGet(type) {
      let _this = this;
      const isLoadMore = type === "more";

      let params = new URLSearchParams();
      params.append("readonly", this.readonly);

      if (this.userid !== null) {
        params.append("user_id", this.userid);
      }

      params.append("category", "user_feelings_log");
      params.append("limit", this.limit);
      params.append("order_by", "created_at");
      params.append("order_direction", "desc");
      params.append("type", "edit");

      if (this.datePreset && this.datePreset !== "custom") {
        params.append("days", this.datePreset);
      } else if (this.datePreset === "custom") {
        if (this.customDateFrom) params.append("date_from", this.customDateFrom);
        if (this.customDateTo) params.append("date_to", this.customDateTo);
      }

      if (isLoadMore) {
        this.loadingMore = true;
        params.append("page", this.currentPage + 1);
      } else {
        this.currentPage = 1;
        this.noMore = false;
        if (type !== "search") {
          this.search_text = "";
        }
        this.searching = 1;
        params.append("page", 1);
      }

      if (type === "search" || (isLoadMore && _this.search_text)) {
        params.append("search_text", _this.search_text);
      }

      axios({
        url: "contractions/get",
        params: params,
      })
        .then( (response) => {
          if (isLoadMore) {
            this.searchFeelingsContractions = this.searchFeelingsContractions.concat(response.data.data);
            this.currentPage += 1;
            this.loadingMore = false;
          } else {
            this.searchFeelingsContractions = response.data.data;
            this.searching = 0;
          }
          this.total = response.data.total;
          this.noMore = response.data.data.length < this.limit;
        })
        .catch(function (error) {
          console.log(error);
          this.loadingMore = false;
          this.searching = 0;
        });
    },
    contractionPriority(contraction) {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      return new Date(contraction.created_at).getTime() >= cutoff ? 'recent' : 'older';
    },
    async statsGet() {
      const params = {};
      if (this.datePreset && this.datePreset !== 'custom') {
        params.days = this.datePreset;
      } else if (this.datePreset === 'custom') {
        if (this.customDateFrom) params.date_from = this.customDateFrom;
        if (this.customDateTo) params.date_to = this.customDateTo;
      }
      this.stats = await axios.get('/dashboard/stats', { params }).then(resp => resp.data);
    },
    onRefresh() {
      this.feelingsGet("latest");
      this.activeRequestsGet();
      this.statsGet();
    },
    clearSearchText() {
      this.search_text = "";
    },
    activeRequestsGet() {
      axios("processingRequests/active")
        .then((response) => {
          this.activeRequests = response.data || [];
        })
        .catch(() => {});
    },

    openForContraction(contraction) {
      this.pendingContractionId = contraction.id
      this.$refs.ackModal.openForContraction(contraction.id);
    },

    openForRequest(req) {
      this.$refs.ackModal.openForRequest(req);
    },
  },
  mounted() {
    this.feelingsGet("latest");
    this.activeRequestsGet();
    this.statsGet();
  },
  watch: {
    search_text(val) {
      clearTimeout(this._searchTimer);
      if (!val) {
        this.feelingsGet("latest");
        return;
      }
      this._searchTimer = setTimeout(() => {
        this.feelingsGet("search");
      }, 400);
    },
  },
};
</script>

<style scoped>
/* Active Processing Requests */
.active-requests {
  background: #fff;
  border: 1px solid rgba(214,182,116,0.25);
  border-radius: 10px;
  padding: 10px 12px;
}

.apr-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.apr-toggle-left {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.apr-toggle-icon {
  font-size: 0.8rem;
  color: #d6b674;
}

.apr-toggle-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #c4a55a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.apr-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(214,182,116,0.18);
  color: #c4a55a;
  font-size: 0.62rem;
  font-weight: 700;
  border-radius: 20px;
  min-width: 17px;
  height: 17px;
  padding: 0 5px;
  line-height: 1;
}

/* Live pulse dot */
.apr-pulse {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(214,182,116,0.6);
  flex-shrink: 0;
}

.apr-chevron {
  font-size: 0.65rem;
  color: #c4a55a;
  opacity: 0.8;
  flex-shrink: 0;
}

.active-requests-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
}

.active-requests-list::-webkit-scrollbar {
  width: 5px;
}
.active-requests-list::-webkit-scrollbar-track {
  background: rgba(214,182,116,0.08);
  border-radius: 4px;
}
.active-requests-list::-webkit-scrollbar-thumb {
  background: rgba(214,182,116,0.5);
  border-radius: 4px;
}

.active-request-row {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(90deg, rgba(214,182,116,0.07) 0%, rgba(214,182,116,0.02) 100%);
  border: 1px solid rgba(214,182,116,0.3);
  border-radius: 10px;
  padding: 10px 14px;
  transition: border-color 0.18s;
}

.active-request-row:hover {
  border-color: rgba(214,182,116,0.4);
}

/* Avatar */
.apr-avatar-wrap {
  flex-shrink: 0;
}

.apr-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(214,182,116,0.35);
  display: block;
}

/* Body */
.apr-body {
  flex: 1;
  min-width: 0;
}

.apr-name {
  font-size: 0.82rem;
  font-weight: 700;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.apr-contraction {
  font-size: 0.75rem;
  color: #888;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Dropdown */
.apr-dropdown {
  flex-shrink: 0;
  margin-right: -10px;
  margin-left: -8px;
}

.apr-dropdown ::v-deep .btn-link {
  padding: 0 2px;
  line-height: 1;
  color: #aaa;
}

.apr-dropdown ::v-deep .btn-link:hover {
  color: #d6b674;
}

.apr-dropdown-icon {
  font-size: 0.9rem;
}

.apr-dropdown ::v-deep .dropdown-item {
  font-size: 0.82rem;
  color: #444;
  padding: 7px 16px;
}

.apr-dropdown ::v-deep .dropdown-item:hover {
  background: rgba(214,182,116,0.1);
  color: #c4a55a;
}

.apr-action-primary ::v-deep a,
.apr-dropdown ::v-deep .apr-action-primary .dropdown-item {
  font-weight: 600;
  color: #d6b674;
}

.active-request-badge {
  flex-shrink: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: 20px;
  padding: 3px 10px;
  /* default (pending) */
  background: rgba(214,182,116,0.15);
  color: #c4a55a;
  border: 1px solid rgba(214,182,116,0.4);
}


/* Date Filter */
.date-filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.date-filter-presets {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.date-filter-presets::-webkit-scrollbar {
  display: none;
}

.date-filter-custom {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

/* Mobile select dropdown */
.date-filter-select-wrap {
  display: none;
  position: relative;
  align-items: center;
}

.date-filter-select-icon {
  position: absolute;
  left: 11px;
  font-size: 0.72rem;
  color: #d6b674;
  pointer-events: none;
  z-index: 1;
}

.date-filter-select {
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  background: rgba(214, 182, 116, 0.08);
  border: 1px solid rgba(214, 182, 116, 0.45);
  border-radius: 20px;
  padding: 6px 36px 6px 30px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #d6b674;
  cursor: pointer;
  outline: none;
}

.date-filter-select:focus {
  border-color: #d6b674;
  box-shadow: 0 0 0 2px rgba(214, 182, 116, 0.15);
}

.date-filter-select-chevron {
  position: absolute;
  right: 12px;
  font-size: 0.65rem;
  color: #d6b674;
  pointer-events: none;
}

.preset-btn {
  background: transparent;
  border: 1px solid rgba(214, 182, 116, 0.35);
  color: #aaa;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.preset-btn:hover {
  border-color: rgba(214, 182, 116, 0.7);
  color: #c4a55a;
}

.preset-btn.active {
  background: rgba(214, 182, 116, 0.18);
  border-color: #d6b674;
  color: #d6b674;
}

.date-input {
  border: 1px solid rgba(214, 182, 116, 0.35);
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 0.72rem;
  color: #555;
  background: #fff;
  outline: none;
  cursor: pointer;
}

.date-input:focus {
  border-color: #d6b674;
}

.date-sep {
  color: #aaa;
  font-size: 0.8rem;
}

/* Stats */
.contraction-stats {
  display: flex;
  gap: 14px;
}

@media (max-width: 600px) {
  .date-filter-presets {
    display: none;
  }
  .date-filter-select-wrap {
    display: flex;
  }
  .apr-toggle-title {
    font-size: 0.7rem;
  }
  .apr-toggle-icon {
    font-size: 0.72rem;
  }
  .apr-toggle-left {
    gap: 5px;
    flex: 1;
    min-width: 0;
  }
  .contraction-stats {
    gap: 6px;
  }
  .active-request-badge {
    font-size: 0.58rem;
    padding: 2px 7px;
  }
  .apr-avatar {
    width: 32px;
    height: 32px;
  }
  .apr-name {
    font-size: 0.74rem;
  }
  .apr-contraction {
    font-size: 0.65rem;
  }
  .apr-dropdown-icon {
    font-size: 0.78rem;
  }
}

@media (max-width: 380px) {
  .contraction-stats {
    gap: 4px;
  }
  .stat-card {
    padding: 8px 4px;
    border-radius: 8px;
  }
  .stat-value {
    font-size: 1.1rem;
  }
  .stat-label {
    font-size: 0.55rem;
    margin-top: 2px;
  }
}

.stat-card {
  flex: 1 1 0;
  min-width: 0;
  background: #fff;
  border: 1px solid rgba(214,182,116,0.25);
  border-radius: 12px;
  padding: 10px 6px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.stat-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #d6b674;
  line-height: 1;
}

.stat-label {
  font-size: 0.65rem;
  color: #999;
  margin-top: 3px;
}

/* Section labels */
.section-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #aaa;
  margin-bottom: 6px;
  padding-left: 2px;
}

.section-label--processed {
  color: #c4a55a;
  margin-top: 18px;
}

.contraction-list-scroll {
  max-height: 320px;
  overflow-y: auto;
  border-radius: 8px;
}

.contraction-list-scroll::-webkit-scrollbar {
  width: 5px;
}
.contraction-list-scroll::-webkit-scrollbar-track {
  background: rgba(214, 182, 116, 0.08);
  border-radius: 4px;
}
.contraction-list-scroll::-webkit-scrollbar-thumb {
  background: rgba(214, 182, 116, 0.5);
  border-radius: 4px;
}

.load-more-btn {
  border-color: rgba(214, 182, 116, 0.4);
  color: #c4a55a;
  font-size: 0.8rem;
  padding: 5px 20px;
  border-radius: 20px;
}

.load-more-btn:hover:not(:disabled) {
  background: rgba(214, 182, 116, 0.1);
  border-color: rgba(214, 182, 116, 0.7);
  color: #b8943e;
}

.load-more-btn:disabled {
  opacity: 0.6;
}


</style>
